/**
 * Compose + Manage modals for TeamTalk (V1.7.9 refactor).
 * Exports ComposeModal, GroupComposeModal, ManageGroupModal.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { UserCircle, UsersThree, MagnifyingGlass, PaperPlaneTilt, SignOut, UserPlus, ArrowRight } from "@phosphor-icons/react";
import { roleLabel } from "./helpers";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import ImageCropperSheet from "@/components/chat/ImageCropperSheet";

// ---------------- shared shells ----------------
function ModalShell({ title, onClose, children, testid }) {
    return (
        <div className="fixed inset-0 bg-ink/50 z-50 flex items-center justify-center p-4" data-testid={testid}>
            <div className="bg-white w-full max-w-md max-h-[90vh] flex flex-col shadow-xl overflow-hidden">
                <div className="px-5 py-4 border-b border-ink/10 flex items-center justify-between">
                    <h2 className="font-heading text-base font-black">{title}</h2>
                    <button type="button" onClick={onClose}
                            className="text-ink/50 hover:text-ink text-xl leading-none"
                            data-testid={`${testid}-close`}>×</button>
                </div>
                <div className="flex flex-col overflow-y-auto">{children}</div>
            </div>
        </div>
    );
}

function SearchBar({ q, onChange, testid, placeholder }) {
    return (
        <div className="px-5 py-3">
            <div className="relative">
                <MagnifyingGlass size={13} weight="bold"
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-ink/40" />
                <input
                    type="text" value={q}
                    onChange={(e) => onChange(e.target.value)}
                    placeholder={placeholder}
                    data-testid={testid}
                    className="w-full pl-9 pr-3 py-2 border border-ink/20 text-sm focus:outline-none focus:border-cobalt"
                />
            </div>
        </div>
    );
}

function EmptyTeammatesState({ q }) {
    const { user } = useAuth();
    const canInvite = user?.role === "admin" || user?.role === "superadmin";
    if (q) {
        return (
            <div className="p-6 text-xs text-ink/50 text-center" data-testid="teamtalk-empty-search">
                No teammates match &quot;{q}&quot;.
            </div>
        );
    }
    return (
        <div className="px-6 py-8 text-center" data-testid="teamtalk-empty-no-teammates">
            <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-cobalt/10 flex items-center justify-center">
                <UsersThree size={20} weight="bold" className="text-cobalt" />
            </div>
            <p className="text-sm font-bold text-ink">You're the first one in your workspace</p>
            <p className="text-xs text-ink/60 mt-1 max-w-xs mx-auto leading-snug">
                Invite a recruiter, hiring manager, or admin from the Team page. They'll appear here once they accept.
            </p>
            {canInvite ? (
                <Link
                    to="/users"
                    data-testid="teamtalk-empty-invite-cta"
                    className="btn-gradient inline-flex items-center gap-1.5 mt-4 px-4 py-2 text-[11px] font-black uppercase tracking-wider"
                >
                    <UserPlus size={12} weight="bold" /> Invite teammates <ArrowRight size={11} weight="bold" />
                </Link>
            ) : (
                <p className="text-[11px] text-ink/50 mt-3 italic">
                    Ask your workspace admin to add teammates.
                </p>
            )}
        </div>
    );
}

function StaffList({ loading, staff, q, onPick, pickTestidPrefix }) {
    return (
        <div className="flex-1 overflow-y-auto" data-testid={`${pickTestidPrefix}staff-list`}>
            {loading ? (
                <div className="p-6 text-xs text-ink/50 text-center">Loading…</div>
            ) : staff.length === 0 ? (
                <EmptyTeammatesState q={q} />
            ) : staff.map((s) => (
                <button key={s.id} type="button"
                    onClick={() => onPick(s)}
                    data-testid={`${pickTestidPrefix}${s.id}`}
                    className="w-full text-left px-5 py-3 border-b border-ink/5 hover:bg-cream/50 flex items-center gap-3">
                    <div className="w-9 h-9 rounded-full bg-cobalt/10 flex items-center justify-center shrink-0">
                        <UserCircle size={20} weight="bold" className="text-cobalt" />
                    </div>
                    <div className="min-w-0 flex-1">
                        <p className="font-bold text-sm truncate">{s.name}</p>
                        <p className="text-[10px] text-ink/50 truncate">{roleLabel(s.role)} · {s.email}</p>
                    </div>
                </button>
            ))}
        </div>
    );
}

function ChosenTargetForm({ target, onBack, body, setBody, busy, onSubmit, onClose, submitTestid, bodyTestid }) {
    return (
        <div className="p-5 space-y-4">
            <div className="flex items-center gap-3 p-3 bg-cream">
                <div className="w-10 h-10 rounded-full bg-cobalt/10 flex items-center justify-center">
                    <UserCircle size={22} weight="bold" className="text-cobalt" />
                </div>
                <div className="min-w-0 flex-1">
                    <p className="font-bold text-sm truncate">{target.name}</p>
                    <p className="text-[10px] text-ink/50 truncate">{roleLabel(target.role)} · {target.email}</p>
                </div>
                <button type="button" onClick={onBack}
                        className="text-[10px] text-cobalt font-black uppercase tracking-wider">
                    Change
                </button>
            </div>
            <textarea
                value={body}
                onChange={(e) => setBody(e.target.value)}
                autoFocus rows={4}
                placeholder="Type your first message…"
                data-testid={bodyTestid}
                className="w-full px-3 py-2 border border-ink/20 text-sm focus:outline-none focus:border-cobalt resize-none"
                maxLength={2000}
            />
            <div className="flex items-center justify-end gap-2">
                <button type="button" onClick={onClose}
                        className="px-3 py-2 text-[11px] font-black uppercase tracking-wider text-ink/60 hover:text-ink">
                    Cancel
                </button>
                <button type="button" onClick={onSubmit}
                        disabled={!body.trim() || busy}
                        data-testid={submitTestid}
                        className="btn-gradient inline-flex items-center gap-1.5 px-4 py-2 text-[11px] font-black uppercase tracking-wider disabled:opacity-40">
                    {busy ? "Sending…" : "Send"}
                    <PaperPlaneTilt size={11} weight="bold" />
                </button>
            </div>
        </div>
    );
}

// ---------------- Compose DM modal ----------------
export function ComposeModal({ onClose, onCreated, initialContext }) {
    const [q, setQ] = useState("");
    const [staff, setStaff] = useState([]);
    const [loading, setLoading] = useState(true);
    const [target, setTarget] = useState(null);
    const [body, setBody] = useState(initialContext?.body || "");
    const [busy, setBusy] = useState(false);

    const loadStaff = useCallback(async (query) => {
        setLoading(true);
        try {
            const res = await api.get(
                `/team-chat/staff${query ? `?q=${encodeURIComponent(query)}` : ""}`,
            );
            setStaff(res?.data?.items || []);
        } catch { /* silent */ } finally { setLoading(false); }
    }, []);

    useEffect(() => { loadStaff(""); }, [loadStaff]);
    useEffect(() => {
        const t = setTimeout(() => loadStaff(q), 250);
        return () => clearTimeout(t);
    }, [q, loadStaff]);

    async function submit() {
        if (!target || !body.trim() || busy) return;
        setBusy(true);
        try {
            const payload = {
                target_user_id: target.id,
                body: body.trim(),
            };
            if (initialContext?.type && initialContext?.id) {
                payload.context_type = initialContext.type;
                payload.context_id = initialContext.id;
                if (initialContext.label) payload.context_label = initialContext.label;
            }
            const res = await api.post("/team-chat/threads", payload);
            toast.success(res?.data?.reused ? "Added to existing chat" : "Chat started");
            onCreated?.(res?.data?.thread);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Couldn't start chat");
        } finally { setBusy(false); }
    }

    return (
        <ModalShell title="New team chat" onClose={onClose} testid="teamtalk-compose-modal">
            {initialContext?.label && (
                <div className="px-5 py-2 bg-cobalt/5 border-b border-cobalt/10">
                    <p className="text-[10px] font-black uppercase tracking-wider text-cobalt" data-testid="teamtalk-compose-context">
                        Linked to {initialContext.type}: {initialContext.label}
                    </p>
                </div>
            )}            {!target ? (
                <>
                    <SearchBar q={q} onChange={setQ} testid="teamtalk-compose-search" placeholder="Search teammates…" />
                    <StaffList
                        loading={loading} staff={staff} q={q}
                        onPick={setTarget}
                        pickTestidPrefix="teamtalk-compose-pick-"
                    />
                </>
            ) : (
                <ChosenTargetForm
                    target={target}
                    onBack={() => setTarget(null)}
                    body={body} setBody={setBody}
                    busy={busy} onSubmit={submit}
                    onClose={onClose}
                    submitTestid="teamtalk-compose-submit"
                    bodyTestid="teamtalk-compose-body"
                />
            )}
        </ModalShell>
    );
}

// ---------------- Compose GROUP modal ----------------
export function GroupComposeModal({ onClose, onCreated, initialContext }) {
    const [q, setQ] = useState("");
    const [staff, setStaff] = useState([]);
    const [loading, setLoading] = useState(true);
    const [name, setName] = useState(
        initialContext?.label ? `About ${initialContext.label}` : "",
    );
    const [picked, setPicked] = useState([]);
    const [body, setBody] = useState(initialContext?.body || "");
    const [busy, setBusy] = useState(false);
    const [pendingImage, setPendingImage] = useState(null);       // File chosen by user
    const [croppedImage, setCroppedImage] = useState(null);       // Blob after crop
    const [previewUrl, setPreviewUrl] = useState(null);           // Object URL for preview
    const imageInputRef = React.useRef(null);

    React.useEffect(() => () => { if (previewUrl) URL.revokeObjectURL(previewUrl); }, [previewUrl]);

    const loadStaff = useCallback(async (query) => {
        setLoading(true);
        try {
            const res = await api.get(`/team-chat/staff${query ? `?q=${encodeURIComponent(query)}` : ""}`);
            setStaff(res?.data?.items || []);
        } catch { /* silent */ } finally { setLoading(false); }
    }, []);

    useEffect(() => { loadStaff(""); }, [loadStaff]);
    useEffect(() => {
        const t = setTimeout(() => loadStaff(q), 250);
        return () => clearTimeout(t);
    }, [q, loadStaff]);

    function togglePick(u) {
        setPicked((prev) => prev.find((x) => x.id === u.id)
            ? prev.filter((x) => x.id !== u.id) : [...prev, u]);
    }

    async function submit() {
        if (!name.trim() || picked.length < 1 || !body.trim() || busy) return;
        setBusy(true);
        try {
            const payload = {
                name: name.trim(),
                member_ids: picked.map((u) => u.id),
                body: body.trim(),
            };
            if (initialContext?.type && initialContext?.id) {
                payload.context_type = initialContext.type;
                payload.context_id = initialContext.id;
                if (initialContext.label) payload.context_label = initialContext.label;
            }
            const res = await api.post("/team-chat/groups", payload);
            const created = res?.data?.thread;
            // V1.9.33 — upload chosen group image after creation.
            if (created?.id && croppedImage) {
                try {
                    const fd = new FormData();
                    fd.append("file", croppedImage, "group.png");
                    const imgRes = await api.post(`/team-chat/groups/${created.id}/image`, fd, {
                        headers: { "Content-Type": "multipart/form-data" },
                    });
                    created.image_url = imgRes.data.image_url;
                } catch { /* non-fatal — group still created */ }
            }
            toast.success("Group created");
            onCreated?.(created);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Couldn't create group");
        } finally { setBusy(false); }
    }

    return (
        <ModalShell title="New group" onClose={onClose} testid="teamtalk-group-compose-modal">
            {initialContext?.label && (
                <div className="px-5 py-2 bg-cobalt/5 border-b border-cobalt/10">
                    <p className="text-[10px] font-black uppercase tracking-wider text-cobalt" data-testid="teamtalk-group-compose-context">
                        Linked to {initialContext.type}: {initialContext.label}
                    </p>
                </div>
            )}            <div className="px-5 py-3 border-b border-ink/10 space-y-3">
                {/* V1.9.33 — group photo picker */}
                <div className="flex items-center gap-3">
                    <button
                        type="button"
                        onClick={() => imageInputRef.current?.click()}
                        className="relative w-14 h-14 rounded-full bg-cobalt/10 border-2 border-dashed border-cobalt/40 flex items-center justify-center overflow-hidden hover:bg-cobalt/20 transition"
                        data-testid="teamtalk-group-image-picker"
                        aria-label="Add group photo"
                    >
                        {previewUrl ? (
                            <img src={previewUrl} alt="Group" className="w-full h-full object-cover" />
                        ) : (
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="text-cobalt"><path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/><circle cx="12" cy="13" r="4"/></svg>
                        )}
                    </button>
                    <input
                        ref={imageInputRef} type="file" accept="image/*" hidden
                        onChange={(e) => {
                            const f = e.target.files?.[0];
                            if (f) setPendingImage(f);
                            e.target.value = "";
                        }}
                    />
                    <div className="text-[11px] text-ink/60 leading-snug">
                        {previewUrl ? "Group photo set — tap to adjust." : "Add a group photo (optional)"}
                    </div>
                </div>
                <input
                    type="text" value={name}
                    onChange={(e) => setName(e.target.value)}
                    autoFocus placeholder="Group name (e.g. Backend Lead Hunt)"
                    data-testid="teamtalk-group-name-input"
                    className="w-full px-3 py-2 border border-ink/20 text-sm focus:outline-none focus:border-cobalt"
                    maxLength={80}
                />
                {picked.length > 0 && (
                    <div className="flex items-center gap-1 flex-wrap">
                        <span className="text-[10px] text-ink/40 uppercase font-black tracking-wider">
                            {picked.length} added:
                        </span>
                        {picked.map((u) => (
                            <span
                                key={u.id}
                                className="inline-flex items-center gap-1 bg-cobalt/10 text-cobalt px-1.5 py-0.5 text-[10px] font-black uppercase tracking-wider"
                                data-testid={`teamtalk-group-picked-${u.id}`}
                            >
                                {u.name}
                                <button type="button" onClick={() => togglePick(u)}
                                        className="hover:text-red-600" aria-label="Remove">×</button>
                            </span>
                        ))}
                    </div>
                )}
                <SearchBar q={q} onChange={setQ} testid="teamtalk-group-search" placeholder="Search teammates to add…" />
            </div>
            <div className="max-h-56 overflow-y-auto" data-testid="teamtalk-group-staff-list">
                {loading ? (
                    <div className="p-4 text-xs text-ink/50 text-center">Loading…</div>
                ) : staff.length === 0 ? (
                    <EmptyTeammatesState q={q} />
                ) : staff.map((s) => {
                    const on = Boolean(picked.find((x) => x.id === s.id));
                    return (
                        <button
                            key={s.id} type="button"
                            onClick={() => togglePick(s)}
                            data-testid={`teamtalk-group-pick-${s.id}`}
                            className={`w-full text-left px-5 py-2.5 border-b border-ink/5 hover:bg-cream/50 flex items-center gap-3 ${on ? "bg-cobalt/5" : ""}`}
                        >
                            <div className={`w-4 h-4 border ${on ? "bg-cobalt border-cobalt" : "border-ink/30"} flex items-center justify-center text-white text-[10px] font-bold`}>
                                {on ? "✓" : ""}
                            </div>
                            <div className="min-w-0 flex-1">
                                <p className="font-bold text-sm truncate">{s.name}</p>
                                <p className="text-[10px] text-ink/50 truncate">
                                    {roleLabel(s.role)} · {s.email}
                                </p>
                            </div>
                        </button>
                    );
                })}
            </div>
            <div className="px-5 py-4 border-t border-ink/10 space-y-3">
                <textarea
                    value={body}
                    onChange={(e) => setBody(e.target.value)}
                    rows={2}
                    placeholder="Type the first message…"
                    data-testid="teamtalk-group-body"
                    className="w-full px-3 py-2 border border-ink/20 text-sm focus:outline-none focus:border-cobalt resize-none"
                    maxLength={2000}
                />
                <div className="flex items-center justify-end gap-2">
                    <button type="button" onClick={onClose}
                            className="px-3 py-2 text-[11px] font-black uppercase tracking-wider text-ink/60 hover:text-ink">
                        Cancel
                    </button>
                    <button
                        type="button" onClick={submit}
                        disabled={busy || !name.trim() || picked.length < 1 || !body.trim()}
                        data-testid="teamtalk-group-submit"
                        className="btn-gradient inline-flex items-center gap-1.5 px-4 py-2 text-[11px] font-black uppercase tracking-wider disabled:opacity-40"
                    >
                        {busy ? "Creating…" : "Create group"}
                        <UsersThree size={11} weight="bold" />
                    </button>
                </div>
            </div>
            <ImageCropperSheet
                file={pendingImage}
                onCancel={() => setPendingImage(null)}
                onCropped={(blob) => {
                    setPendingImage(null);
                    setCroppedImage(blob);
                    const url = URL.createObjectURL(blob);
                    setPreviewUrl((prev) => { if (prev) URL.revokeObjectURL(prev); return url; });
                }}
                title="Adjust group photo"
            />
        </ModalShell>
    );
}

// ---------------- Manage group modal ----------------
export function ManageGroupModal({ thread, members, onClose, onChanged, onLeft }) {
    const [name, setName] = useState(thread.name || "");
    const [busy, setBusy] = useState(false);
    const [addOpen, setAddOpen] = useState(false);
    const [addQ, setAddQ] = useState("");
    const [addStaff, setAddStaff] = useState([]);
    const memberIds = useMemo(() => new Set(members.map((m) => m.id)), [members]);

    async function rename() {
        if (!name.trim() || name === thread.name || busy) return;
        setBusy(true);
        try {
            await api.patch(`/team-chat/groups/${thread.id}`, { name: name.trim() });
            toast.success("Group renamed");
            await onChanged?.();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Couldn't rename");
        } finally { setBusy(false); }
    }
    async function removeMember(uid) {
        if (busy) return;
        setBusy(true);
        try {
            await api.patch(`/team-chat/groups/${thread.id}`, { remove_member_ids: [uid] });
            await onChanged?.();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Couldn't remove");
        } finally { setBusy(false); }
    }
    async function leave() {
        if (busy) return;
        if (!window.confirm("Leave this group?")) return;
        setBusy(true);
        try {
            await api.post(`/team-chat/groups/${thread.id}/leave`);
            toast.success("Left the group");
            onLeft?.();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Couldn't leave");
            setBusy(false);
        }
    }
    async function loadAddCandidates(qq) {
        try {
            const res = await api.get(`/team-chat/staff${qq ? `?q=${encodeURIComponent(qq)}` : ""}`);
            setAddStaff((res?.data?.items || []).filter((s) => !memberIds.has(s.id)));
        } catch { /* silent */ }
    }
    useEffect(() => {
        if (!addOpen) return;
        const t = setTimeout(() => loadAddCandidates(addQ), 250);
        return () => clearTimeout(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [addQ, addOpen]);

    async function addOne(u) {
        if (busy) return;
        setBusy(true);
        try {
            await api.patch(`/team-chat/groups/${thread.id}`, { add_member_ids: [u.id] });
            toast.success(`Added ${u.name}`);
            await onChanged?.();
            setAddOpen(false); setAddQ("");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Couldn't add");
        } finally { setBusy(false); }
    }

    return (
        <ModalShell title="Group details" onClose={onClose} testid="teamtalk-manage-modal">
            <div className="p-5 space-y-4">
                <div>
                    <label className="text-[10px] font-black uppercase tracking-wider text-ink/50">Name</label>
                    <div className="flex gap-2 mt-1">
                        <input
                            type="text" value={name}
                            onChange={(e) => setName(e.target.value)}
                            data-testid="teamtalk-manage-name-input"
                            className="flex-1 px-3 py-2 border border-ink/20 text-sm focus:outline-none focus:border-cobalt"
                            maxLength={80}
                        />
                        <button
                            type="button" onClick={rename}
                            disabled={busy || !name.trim() || name === thread.name}
                            data-testid="teamtalk-manage-rename-btn"
                            className="px-3 py-2 bg-cobalt text-white text-[11px] font-black uppercase tracking-wider disabled:opacity-40"
                        >
                            Save
                        </button>
                    </div>
                </div>

                <div>
                    <div className="flex items-center justify-between mb-1">
                        <label className="text-[10px] font-black uppercase tracking-wider text-ink/50">
                            {members.length} Members
                        </label>
                        <button type="button" onClick={() => setAddOpen((x) => !x)}
                                data-testid="teamtalk-manage-toggle-add"
                                className="text-[10px] font-black uppercase tracking-wider text-cobalt hover:underline">
                            {addOpen ? "Cancel" : "+ Add member"}
                        </button>
                    </div>

                    {addOpen && (
                        <div className="border border-ink/10 mb-2">
                            <SearchBar q={addQ} onChange={setAddQ} testid="teamtalk-manage-search" placeholder="Search teammates…" />
                            <div className="max-h-40 overflow-y-auto" data-testid="teamtalk-manage-add-list">
                                {addStaff.length === 0 ? (
                                    <div className="p-3 text-xs text-ink/50 text-center">No one to add</div>
                                ) : addStaff.map((s) => (
                                    <button key={s.id} type="button" onClick={() => addOne(s)}
                                            data-testid={`teamtalk-manage-add-${s.id}`}
                                            className="w-full text-left px-4 py-2 border-b border-ink/5 hover:bg-cream/50 flex items-center gap-2">
                                        <UserCircle size={16} weight="bold" className="text-cobalt" />
                                        <span className="font-bold text-sm truncate flex-1">{s.name}</span>
                                        <span className="text-[10px] text-ink/40 uppercase">{roleLabel(s.role)}</span>
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    <div className="border border-ink/10 divide-y divide-ink/5 max-h-64 overflow-y-auto">
                        {members.map((m) => (
                            <div key={m.id}
                                 className="flex items-center gap-3 px-3 py-2"
                                 data-testid={`teamtalk-manage-member-${m.id}`}>
                                <UserCircle size={18} weight="bold" className="text-cobalt" />
                                <div className="min-w-0 flex-1">
                                    <p className="font-bold text-sm truncate">
                                        {m.name}
                                        {m.id === thread.created_by && (
                                            <span className="ml-1.5 text-[9px] font-black uppercase tracking-wider bg-amber-100 text-amber-800 px-1 py-0.5">Creator</span>
                                        )}
                                    </p>
                                    <p className="text-[10px] text-ink/50 truncate">{roleLabel(m.role)} · {m.email}</p>
                                </div>
                                <button type="button" onClick={() => removeMember(m.id)}
                                        disabled={busy}
                                        data-testid={`teamtalk-manage-remove-${m.id}`}
                                        className="text-[10px] font-black uppercase tracking-wider text-red-600 hover:underline disabled:opacity-40">
                                    Remove
                                </button>
                            </div>
                        ))}
                    </div>
                </div>

                <div className="border-t border-ink/10 pt-4 flex items-center justify-between">
                    <button type="button" onClick={leave}
                            disabled={busy}
                            data-testid="teamtalk-manage-leave-btn"
                            className="inline-flex items-center gap-1.5 px-3 py-2 text-[11px] font-black uppercase tracking-wider text-red-600 border border-red-200 hover:bg-red-50">
                        <SignOut size={12} weight="bold" /> Leave group
                    </button>
                    <button type="button" onClick={onClose}
                            className="px-3 py-2 text-[11px] font-black uppercase tracking-wider text-ink/60 hover:text-ink">
                        Done
                    </button>
                </div>
            </div>
        </ModalShell>
    );
}
